// focusActive — pure helper that, given a list of focus_rules + a clock
// instant, returns the rule whose local-time window contains that instant
// in the rule's IANA timezone. Used by the Focus page to render the
// "Active now" treatment + the "Active rule now" stat tile.
//
// Why this is non-trivial: focus_rules.start_time and end_time are stored
// as 'HH:MM:SS' local-clock fields with a separate `timezone` column
// (e.g. 'America/Los_Angeles'). The clock instant we compare against is
// a JS Date (UTC underneath). To decide "is now inside Joel's 09:00-11:00
// LA block?", we must extract the wall-clock hour/minute/second AND
// day-of-week in the rule's tz — not the browser's tz.
//
// We do that with Intl.DateTimeFormat({ timeZone: rule.timezone }), which
// honors DST automatically and works in every modern browser.
//
// Window semantics: half-open [start, end). Exact `start_time` is
// active; exact `end_time` is NOT (the next instant after end is no
// longer active). This is the standard calendar convention and avoids
// two adjacent rules both being "active now" at the seam.
//
// Multi-rule tiebreak: if more than one rule's window contains `now`
// (overlapping config — unusual but legal), return the first one in
// the input order. Callers that want a different policy can pre-sort.
//
// Inactive rules (`is_active=false`) are skipped entirely.

export type FocusRuleForActive = {
  id: string;
  day_of_week: number; // 0=Sun … 6=Sat
  start_time: string; // 'HH:MM:SS'
  end_time: string; // 'HH:MM:SS'
  timezone: string | null;
  is_active: boolean;
};

const FALLBACK_TZ = 'America/Los_Angeles';

// Cache one formatter per (tz). Constructing Intl.DateTimeFormat is
// non-trivial; reuse pays for itself even for a short list of rules.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache.get(tz);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      // Invalid IANA name → fall back to the project default (LA). We
      // cache the fallback under the bad key so subsequent calls don't
      // re-throw.
      f = new Intl.DateTimeFormat('en-US', {
        timeZone: FALLBACK_TZ,
        hour12: false,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    }
    formatterCache.set(tz, f);
  }
  return f;
}

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

type Wall = { dayOfWeek: number; secondsOfDay: number };

function extractWall(now: Date, tz: string): Wall {
  const parts = getFormatter(tz).formatToParts(now);
  let weekday = 'Sun';
  let hour = 0;
  let minute = 0;
  let second = 0;
  for (const p of parts) {
    if (p.type === 'weekday') weekday = p.value;
    else if (p.type === 'hour') hour = Number(p.value);
    else if (p.type === 'minute') minute = Number(p.value);
    else if (p.type === 'second') second = Number(p.value);
  }
  // Intl in hour12:false mode emits "24" for midnight in some browsers
  // (Chrome legacy). Normalize to 0.
  if (hour === 24) hour = 0;
  return {
    dayOfWeek: WEEKDAY_TO_INDEX[weekday] ?? 0,
    secondsOfDay: hour * 3600 + minute * 60 + second,
  };
}

function parseHms(hms: string): number {
  // Expect 'HH:MM:SS' but tolerate 'HH:MM' (no seconds).
  const parts = hms.split(':').map((p) => Number(p));
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const s = parts[2] ?? 0;
  return h * 3600 + m * 60 + s;
}

/**
 * Return the first rule whose window contains `now`, or null if none do.
 * Window semantics: [start_time, end_time) in the rule's IANA timezone.
 * Falls back to America/Los_Angeles when the rule's timezone is null or
 * a string Intl.DateTimeFormat rejects.
 */
export function findActiveFocusRule<R extends FocusRuleForActive>(rules: R[], now: Date): R | null {
  for (const rule of rules) {
    if (!rule.is_active) continue;
    const tz = rule.timezone && rule.timezone.length > 0 ? rule.timezone : FALLBACK_TZ;
    const wall = extractWall(now, tz);
    if (wall.dayOfWeek !== rule.day_of_week) continue;
    const startS = parseHms(rule.start_time);
    const endS = parseHms(rule.end_time);
    if (wall.secondsOfDay >= startS && wall.secondsOfDay < endS) {
      return rule;
    }
  }
  return null;
}

/** Test-only — clear the formatter cache between tz-mutating cases. */
export function __clearFocusActiveCache(): void {
  formatterCache.clear();
}
